/**
 * Featured Posts Normalizer
 * 
 * Handles backward compatibility for legacy Featured Posts data formats.
 * Normalizes various legacy shapes into the current FeaturedPost format.
 */

/**
 * Normalized Featured Post format
 * 
 * Note: This uses `url` as the primary field name, but components may expect
 * `videoUrl` or `contentUrl`. Components should handle both for backward compatibility.
 */
export type FeaturedPost = {
  id: string;
  url: string; // Primary field name (normalized format)
  platform: "tiktok" | "instagram" | "youtube" | "unknown";
  thumbnailUrl?: string;
  title?: string;
  thumbnailUpdatedAt?: number;
  // Legacy field names for backward compatibility (components can use these)
  videoUrl?: string; // Alias for url (for ProfileFeaturedPostsSection)
  contentUrl?: string; // Alias for url (for FeaturedPostsSection)
};

/**
 * Generate a UUID - uses crypto.randomUUID() if available, otherwise fallback
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback UUID v4 generator
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Check if a string is a valid URL
 */
function isValidUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }
  
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Detect platform from URL
 */
function detectPlatform(url: string): "tiktok" | "instagram" | "youtube" | "unknown" {
  if (!url) return "unknown";
  
  const lowerUrl = url.toLowerCase();
  
  if (lowerUrl.includes('tiktok.com')) {
    return "tiktok";
  }
  
  if (lowerUrl.includes('instagram.com') || lowerUrl.includes('instagr.am')) {
    return "instagram";
  }

  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    return "youtube";
  }
  
  return "unknown";
}

/**
 * Normalize a single featured post item (handles various legacy formats)
 */
function normalizePostItem(item: unknown): FeaturedPost | null {
  // Handle string format: "https://tiktok.com/..." or "https://instagram.com/..."
  if (typeof item === 'string') {
    if (!isValidUrl(item)) {
      return null;
    }
    const platform = detectPlatform(item);
    return {
      id: generateUUID(),
      url: item,
      platform,
      // Add aliases for backward compatibility with components
      videoUrl: item,
      contentUrl: item,
    };
  }

  // Handle object format
  if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
    const obj = item as Record<string, unknown>;
    
    // Extract URL from various possible keys
    const url = 
      (typeof obj.url === 'string' ? obj.url : null) ||
      (typeof obj.link === 'string' ? obj.link : null) ||
      (typeof obj.contentUrl === 'string' ? obj.contentUrl : null) ||
      (typeof obj.videoUrl === 'string' ? obj.videoUrl : null) ||
      null;

    if (!url || !isValidUrl(url)) {
      return null;
    }

    // Extract thumbnail from various possible keys
    const thumbnailUrl = 
      (typeof obj.thumbnailUrl === 'string' ? obj.thumbnailUrl : null) ||
      (typeof obj.thumbnail === 'string' ? obj.thumbnail : null) ||
      (typeof obj.thumbnail_url === 'string' ? obj.thumbnail_url : null) ||
      (typeof obj.imageUrl === 'string' ? obj.imageUrl : null) ||
      null;

    // Extract title
    const title = typeof obj.title === 'string' ? obj.title : undefined;

    // Extract ID or generate one
    const id = typeof obj.id === 'string' ? obj.id : generateUUID();

    // Extract thumbnailUpdatedAt if present
    const thumbnailUpdatedAt = 
      typeof obj.thumbnailUpdatedAt === 'number' ? obj.thumbnailUpdatedAt :
      typeof obj.thumbnailUpdatedAt === 'object' && obj.thumbnailUpdatedAt !== null ? 
        (obj.thumbnailUpdatedAt as any).toMillis?.() || undefined :
      undefined;

    const platform = detectPlatform(url);
    
    return {
      id,
      url,
      platform,
      thumbnailUrl: thumbnailUrl || undefined,
      title,
      thumbnailUpdatedAt,
      // Add aliases for backward compatibility with components
      videoUrl: url,
      contentUrl: url,
    };
  }

  // Invalid format
  return null;
}

/**
 * Normalize featured posts array from various legacy formats
 * 
 * Supports:
 * - string[] - Array of TikTok URLs
 * - [{ url: string }] - Array of objects with url
 * - [{ link: string }] - Array of objects with link
 * - [{ contentUrl: string }] - Array of objects with contentUrl
 * - [{ thumbnailUrl: string, ... }] - Current format
 * - [{ thumbnail: string, ... }] - Legacy thumbnail key
 * - [{ thumbnail_url: string, ... }] - Legacy thumbnail_url key
 * 
 * @param input - Unknown input (could be any legacy format)
 * @returns Normalized array of FeaturedPost objects
 */
export function normalizeFeaturedPosts(input: unknown): FeaturedPost[] {
  // Handle null/undefined
  if (!input) {
    return [];
  }

  // Debug logging in development
  if (process.env.NODE_ENV === 'development' || typeof window !== 'undefined') {
    console.log('[normalizeFeaturedPosts] Input type:', typeof input, Array.isArray(input) ? 'array' : 'object');
    if (Array.isArray(input) && input.length > 0) {
      console.log('[normalizeFeaturedPosts] First item type:', typeof input[0], input[0]);
    }
  }

  // Handle array
  if (Array.isArray(input)) {
    const normalized: FeaturedPost[] = [];
    
    for (const item of input) {
      const normalizedItem = normalizePostItem(item);
      if (normalizedItem) {
        normalized.push(normalizedItem);
      }
    }
    
    // Debug logging
    if (process.env.NODE_ENV === 'development' || typeof window !== 'undefined') {
      console.log(`[normalizeFeaturedPosts] Normalized ${normalized.length} post(s) from ${input.length} input item(s)`);
    }
    
    return normalized;
  }

  // Handle single item (non-array)
  const normalizedItem = normalizePostItem(input);
  return normalizedItem ? [normalizedItem] : [];
}


/**
 * Group featured posts by platform
 * 
 * @param posts - Normalized featured posts array
 * @returns Record of platform -> posts array
 */
export function groupFeaturedPostsByPlatform(posts: FeaturedPost[]): Record<string, FeaturedPost[]> {
  const groups: Record<string, FeaturedPost[]> = {
    tiktok: [],
    instagram: [],
    youtube: [],
    unknown: [],
  };
  
  for (const post of posts) {
    const platform = post.platform || 'unknown';
    if (!groups[platform]) {
      groups[platform] = [];
    }
    groups[platform].push(post);
  }
  
  return groups;
}

/**
 * Get available platforms from grouped posts (excludes empty and unknown)
 * 
 * @param grouped - Grouped posts by platform
 * @returns Array of platform keys that have posts
 */
export function getAvailablePlatforms(grouped: Record<string, FeaturedPost[]>): string[] {
  return Object.keys(grouped).filter(
    platform => grouped[platform].length > 0 && platform !== 'unknown'
  );
}

/**
 * Platform display names
 */
export const PLATFORM_LABELS: Record<string, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  youtube: 'YouTube',
  unknown: 'Other',
};
